import { request } from '../../api';

export type PlanPeriod = 'month' | 'quarter' | 'year';

export interface PlanLimits {
  readonly usesPerPeriod?: number;
  readonly usesPerDay?: number;
  readonly minIntervalHours?: number;
  /** Сколько машин покрывает подписка; больше одной — семейный план. */
  readonly maxVehicles?: number;
}

export interface Plan {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly period: PlanPeriod;
  readonly priceMinor: number;
  readonly currency: string;
  readonly limits: PlanLimits;
  readonly status: 'active' | 'archived';
  readonly createdAt: string;
}

export interface Subscription {
  readonly id: string;
  readonly customerId: string;
  readonly planCode: string;
  readonly vehicleId: string;
  readonly status: string;
  readonly currentPeriodEnd: string | null;
  readonly autorenew: boolean;
  readonly cancelReason: string | null;
  readonly chargeAttempts: number;
}

export interface ChargeRow {
  readonly id: string;
  readonly period_key: string;
  readonly attempt: number;
  readonly status: string;
  readonly failure_reason: string | null;
  readonly amount_minor: string;
  readonly next_attempt_at: string | null;
  readonly external_id: string | null;
  readonly created_at: string;
}

export interface TransitionRow {
  readonly from_status: string;
  readonly to_status: string;
  readonly reason: string | null;
  readonly created_at: string;
}

export interface SubscriptionCard {
  readonly subscription: Subscription;
  readonly charges: readonly ChargeRow[];
  readonly transitions: readonly TransitionRow[];
}

export interface Coverage {
  readonly covered: boolean;
  readonly reason: string;
  readonly subscriptionId: string | null;
  readonly planCode: string | null;
  readonly usesLeft: number | null;
  readonly usedInPeriod: number;
  readonly creditsLeft: number;
  readonly periodEnd: string | null;
  readonly nextAllowedAt: string | null;
}

export interface ChurnBucket {
  readonly month: string;
  readonly activeAtStart: number;
  readonly activated: number;
  readonly cancelled: number;
  readonly expired: number;
  readonly activeAtEnd: number;
  readonly churnRate: number;
}

export interface PlanChurn {
  readonly planId: string;
  readonly planCode: string;
  readonly activeAtStart: number;
  readonly activated: number;
  readonly cancelled: number;
  readonly churnRate: number;
}

export interface ChurnReport {
  readonly from: string;
  readonly to: string;
  readonly activeAtStart: number;
  readonly activated: number;
  readonly cancelled: number;
  readonly expired: number;
  readonly activeAtEnd: number;
  readonly churnRate: number;
  readonly buckets: readonly ChurnBucket[];
  readonly byPlan: readonly PlanChurn[];
}

export interface ChargePolicy {
  readonly siteId: string | null;
  readonly maxAttempts: number;
  readonly retryIntervalHours: readonly number[];
  readonly gracePeriodHours: number;
  readonly reminderLeadDays: number;
}

export interface UsagePolicy {
  readonly siteId: string | null;
  readonly maxUsesPerPeriod: number | null;
  readonly maxUsesPerDay: number;
  readonly minHoursBetweenUses: number;
  readonly enforceVehicleBinding: boolean;
}

export interface ConversionPolicy {
  readonly strategy: 'carry_over' | 'extend_period' | 'customer_choice';
}

export interface Policies {
  readonly charge: ChargePolicy;
  readonly usage: UsagePolicy;
  readonly conversion: ConversionPolicy;
}

export async function fetchPlans(status?: 'active' | 'archived'): Promise<readonly Plan[]> {
  const query = status ? `?status=${status}` : '';
  const data = await request<{ plans: readonly Plan[] }>(
    'GET',
    `/api/v1/subscriptions/plans${query}`,
  );
  return data.plans;
}

export interface PlanDraft {
  readonly code: string;
  readonly name: string;
  readonly period: PlanPeriod;
  readonly priceMinor: number;
  readonly limits?: PlanLimits;
}

export async function createPlan(draft: PlanDraft): Promise<Plan> {
  const data = await request<{ plan: Plan }>('POST', '/api/v1/subscriptions/plans', draft);
  return data.plan;
}

export async function updatePlan(
  id: string,
  patch: Partial<PlanDraft> & { readonly status?: 'active' | 'archived' },
): Promise<Plan> {
  const data = await request<{ plan: Plan }>(
    'PATCH',
    `/api/v1/subscriptions/plans/${id}`,
    patch,
  );
  return data.plan;
}

export async function fetchSubscription(id: string): Promise<SubscriptionCard> {
  return request<SubscriptionCard>('GET', `/api/v1/subscriptions/${id}`);
}

export async function checkVehicle(vehicleId: string): Promise<Coverage> {
  return request<Coverage>(
    'GET',
    `/api/v1/subscriptions/check?vehicle_id=${encodeURIComponent(vehicleId)}`,
  );
}

export async function pauseSubscription(id: string, reason?: string): Promise<Subscription> {
  const data = await request<{ subscription: Subscription }>(
    'POST',
    `/api/v1/subscriptions/${id}/pause`,
    reason ? { reason } : {},
  );
  return data.subscription;
}

export async function resumeSubscription(id: string): Promise<Subscription> {
  const data = await request<{ subscription: Subscription }>(
    'POST',
    `/api/v1/subscriptions/${id}/resume`,
  );
  return data.subscription;
}

export async function cancelSubscription(id: string, reason: string): Promise<Subscription> {
  const data = await request<{ subscription: Subscription }>(
    'POST',
    `/api/v1/subscriptions/${id}/cancel`,
    { reason },
  );
  return data.subscription;
}

export async function fetchChurn(
  from: string,
  to: string,
  planId?: string,
): Promise<ChurnReport> {
  const query = new URLSearchParams({ from, to });
  if (planId) query.set('plan_id', planId);
  return request<ChurnReport>('GET', `/api/v1/subscriptions/churn?${query.toString()}`);
}

export async function fetchPolicies(): Promise<Policies> {
  return request<Policies>('GET', '/api/v1/subscriptions/policies');
}

export async function saveChargePolicy(
  patch: Partial<ChargePolicy>,
): Promise<ChargePolicy> {
  const data = await request<{ charge: ChargePolicy }>(
    'POST',
    '/api/v1/subscriptions/policies/charge',
    patch,
  );
  return data.charge;
}

export async function saveUsagePolicy(patch: Partial<UsagePolicy>): Promise<UsagePolicy> {
  const data = await request<{ usage: UsagePolicy }>(
    'POST',
    '/api/v1/subscriptions/policies/usage',
    patch,
  );
  return data.usage;
}

export async function saveConversionPolicy(
  strategy: ConversionPolicy['strategy'],
): Promise<ConversionPolicy> {
  const data = await request<{ conversion: ConversionPolicy }>(
    'POST',
    '/api/v1/subscriptions/policies/conversion',
    { strategy },
  );
  return data.conversion;
}

/**
 * Подписки клиента.
 *
 * Маршрут был написан и снабжён комментарием «функция была экспортирована, но
 * ни один маршрут её не вызывал», а затем повторил ту же судьбу на уровне
 * интерфейса: карточку подписки можно было открыть, только зная идентификатор
 * самой подписки или автомобиля. Откуда их взять — не объяснялось.
 */
export async function subscriptionsByCustomer(
  customerId: string,
): Promise<readonly Subscription[]> {
  const data = await request<{ subscriptions: readonly Subscription[] }>(
    'GET',
    `/api/v1/subscriptions?customer_id=${encodeURIComponent(customerId)}`,
  );
  return data.subscriptions;
}

/**
 * Ручное списание.
 *
 * Ключ в заголовке требует маршрут, но защиту от двойного платежа держит не он:
 * `runCharge` идемпотентен по паре «подписка + период», поэтому второе нажатие
 * вернёт то же самое списание, а не создаст второе.
 */
export async function chargeSubscription(id: string): Promise<unknown> {
  return request(
    'POST',
    `/api/v1/subscriptions/${id}/charge`,
    {},
    { idempotencyKey: `admin-${crypto.randomUUID()}` },
  );
}

/* --------------------------------------------- оформление и состав подписки */

export interface CatalogPlan {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly period: PlanPeriod;
  readonly priceMinor: number;
  readonly limits: PlanLimits;
}

/** Планы в продаже: открыты тем, кто продаёт подписку (кассир, управляющий). */
export async function fetchCatalog(): Promise<readonly CatalogPlan[]> {
  const data = await request<{ plans: readonly CatalogPlan[] }>(
    'GET',
    '/api/v1/subscriptions/catalog',
  );
  return data.plans;
}

export interface CustomerVehicle {
  readonly id: string;
  readonly plate: string;
  readonly role: string;
  readonly valid_to: string | null;
}

export async function customerVehicles(customerId: string): Promise<readonly CustomerVehicle[]> {
  const data = await request<{ vehicles: readonly CustomerVehicle[] }>(
    'GET',
    `/api/v1/customers/${encodeURIComponent(customerId)}`,
  );
  return data.vehicles.filter((v) => v.valid_to === null);
}

export interface EnrollDraft {
  readonly customerId: string;
  readonly planId: string;
  readonly vehicleId: string;
  readonly autorenew: boolean;
  readonly chargeNow: boolean;
}

export interface EnrollResult {
  readonly subscription: Subscription;
  readonly charge: { readonly status: string } | null;
}

export async function enroll(draft: EnrollDraft): Promise<EnrollResult> {
  return request<EnrollResult>('POST', '/api/v1/subscriptions', draft, {
    idempotencyKey: `admin-${crypto.randomUUID()}`,
  });
}

export interface SubscriptionVehicle {
  readonly vehicleId: string;
  readonly plate: string;
  readonly role: 'primary' | 'member';
  readonly addedAt: string;
  readonly removedAt: string | null;
  readonly removeReason: string | null;
  readonly linked: boolean;
}

export async function subscriptionVehicles(id: string): Promise<readonly SubscriptionVehicle[]> {
  const data = await request<{ vehicles: readonly SubscriptionVehicle[] }>(
    'GET',
    `/api/v1/subscriptions/${id}/vehicles`,
  );
  return data.vehicles;
}

export async function changeVehicle(
  id: string,
  vehicleId: string,
  releasePrevious: boolean,
): Promise<unknown> {
  return request('POST', `/api/v1/subscriptions/${id}/vehicle`, { vehicleId, releasePrevious });
}

export async function addVehicle(
  id: string,
  vehicleId: string,
  linkAsDriver: boolean,
): Promise<unknown> {
  return request('POST', `/api/v1/subscriptions/${id}/vehicles`, { vehicleId, linkAsDriver });
}

export async function removeVehicle(id: string, vehicleId: string, reason: string): Promise<unknown> {
  return request('POST', `/api/v1/subscriptions/${id}/vehicles/${vehicleId}/remove`, { reason });
}

/* ------------------------------------------------------ удержание и возврат */

export type RetentionCode = 'dunning' | 'renewal_reminder' | 'cancel_save' | 'winback' | 'return';

export interface RetentionScenario {
  readonly code: RetentionCode;
  readonly enabled: boolean;
  readonly delayDays: number;
  readonly cooldownDays: number;
  readonly offer: string;
}

export interface RetentionTouch {
  readonly id: string;
  readonly scenario: RetentionCode;
  readonly customerId: string;
  readonly customerName: string | null;
  readonly subscriptionId: string | null;
  readonly createdAt: string;
}

export async function fetchRetention(): Promise<{
  readonly scenarios: readonly RetentionScenario[];
  readonly touches: readonly RetentionTouch[];
}> {
  return request('GET', '/api/v1/subscriptions/retention');
}

export async function saveRetention(scenario: RetentionScenario): Promise<RetentionScenario> {
  const data = await request<{ scenario: RetentionScenario }>(
    'POST',
    `/api/v1/subscriptions/retention/${scenario.code}`,
    {
      enabled: scenario.enabled,
      delayDays: scenario.delayDays,
      cooldownDays: scenario.cooldownDays,
      offer: scenario.offer,
    },
  );
  return data.scenario;
}

export async function runRetentionNow(): Promise<{ readonly total: number }> {
  return request<{ total: number }>('POST', '/api/v1/subscriptions/retention/run');
}
